import { usePDF } from "react-to-pdf";

/**
 * Global PDF utility function with US Letter format
 * @param {string} filename - The name of the PDF file (optional)
 * @param {object} options - Additional PDF options (optional)
 * @returns {object} - Returns { toPDF, targetRef } from usePDF hook
 */
export const useGlobalPDF = (filename = "document.pdf", options = {}) => {
  const defaultOptions = {
    filename: filename,
    page: {
      format: "A4", // US Letter format in points (8.5 x 11 inches)
      orientation: "portrait",
      margin: 10,
    },
    scale: 0.95,
    ...options, // Allow overriding default options
  };

  const { toPDF, targetRef } = usePDF(defaultOptions);

  /**
   * Enhanced PDF download function that handles no-print elements
   * @param {function} callback - Optional callback function to run after PDF generation
   */
  const downloadPDF = (callback) => {
    const noPrintElements = document.querySelectorAll(".no-print");

    // Hide no-print elements before generating the PDF
    noPrintElements.forEach((el) => (el.style.display = "none"));

    // Generate the PDF
    toPDF().then(() => {
      // Show no-print elements after PDF is generated
      noPrintElements.forEach((el) => (el.style.display = ""));
      
      // Execute callback if provided
      if (callback && typeof callback === 'function') {
        callback();
      }
    });
  };

  return {
    toPDF,
    targetRef,
    downloadPDF,
  };
};

/**
 * Alternative function for direct PDF generation without React hook
 * Useful for components that don't need the ref
 * @param {HTMLElement} element - The DOM element to convert to PDF
 * @param {string} filename - The name of the PDF file
 * @param {object} options - Additional PDF options
 */
export const generatePDF = async (element, filename = "document.pdf", options = {}) => {
  const { jsPDF } = await import('jspdf');
  const html2canvas = await import('html2canvas');
  
  const canvas = await html2canvas.default(element, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
  });
  
  const imgData = canvas.toDataURL('image/png');
  const pdf = new jsPDF.default({
    orientation: 'portrait',
    unit: 'pt',
    format: [612, 792], // US Letter format
  });
  
  const imgWidth = 612;
  const pageHeight = 792;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;
  let heightLeft = imgHeight;
  let position = 0;
  
  pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
  heightLeft -= pageHeight;
  
  while (heightLeft >= 0) {
    position = heightLeft - imgHeight;
    pdf.addPage();
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
  }
  
  pdf.save(filename);
}; 